import React, { useState } from "react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LogOut, User, Sun, Moon, Shield, Menu, X, Activity, ClipboardList, Info, HelpCircle } from "lucide-react";
import { RestartTourButton } from "@/components/GuidedTour";
import DilBudgetMeter from "@/components/DilBudgetMeter";
import CoachNudgeBell from "@/components/dashboard/CoachNudgeBell";
import StreakBadge from "@/components/StreakBadge";
import InsulinConceptsModal from "@/components/InsulinConceptsModal";
import DashboardCustomizerDrawer from "@/dashboard/DashboardCustomizerDrawer";  // iter549
import DashboardEditModeToggle from "@/dashboard/DashboardEditModeToggle";  // iter551
import ModeToggle from "@/dashboard/ModeToggle";  // iter561 — Easy/Standard/Expert disclosure

export default function DashboardHeader({ activePanel, setActivePanel, onEditProfile, onOpenMore }) {
  const { user, logout } = useAuth();
  const { t, language, setLanguage, SUPPORTED_LANGUAGES } = useLanguage();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();  // iter558 — only show customize on /dashboard (NOT on /dashboard/profile etc.)
  const isDashboard = location.pathname === "/dashboard";
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showConcepts, setShowConcepts] = useState(false);
  const bg = language === "bg";

  const handleLogout = async () => {
    // Навигация към началната страница ПРЕДИ да нулираме user state,
    // за да не позволим на ProtectedRoute да пренасочи към /login.
    navigate("/", { replace: true });
    await logout();
  };

  return (
    <header className={`sticky top-0 z-50 border-b pt-[env(safe-area-inset-top)] ${isDark ? 'bg-[hsl(222,47%,8%)] border-[hsl(223,47%,18%)]' : 'bg-white border-[#E2E8F0]'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo (wordmark вграден в PNG) */}
          <div className="flex items-center cursor-pointer" onClick={() => navigate('/dashboard')} data-testid="logo">
            {/* iter1078 — на телефон само иконата (текстовото лого се смаляваше и не се четеше) */}
            <img src="/app-icon.png" alt="Nutramilo" className="h-9 w-9 object-contain sm:hidden" data-testid="logo-mark-mobile" />
            <img src="/nutramilo-logo.png" alt="Nutramilo" className="hidden sm:block h-12 w-auto object-contain" />
          </div>

          {/* Right Side — AppShell tabs (Табло/Дневник/…) handle primary navigation */}
          <div className="flex items-center space-x-1 sm:space-x-2">
            {/* Adherence streak — patient habit-loop. Mobile-friendly compact pill. */}
            <StreakBadge />
            {/* iter419 — "?" button → InsulinConceptsModal: какво всъщност значат NIS, DIL, VAIS */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowConcepts(true)}
              data-testid="insulin-concepts-trigger"
              title={bg ? "Какво означават NIS, DIL и VAIS?" : "What do NIS, DIL and VAIS mean?"}
              className={`h-8 w-8 sm:h-9 sm:w-9 rounded-lg ${isDark ? "text-gray-400 hover:text-white hover:bg-[hsl(223,47%,15%)]" : "text-[#64748B] hover:text-[#1A1A2E] hover:bg-[#F1F5F9]"}`}
            >
              <HelpCircle className="w-4 h-4 sm:w-5 sm:h-5" />
            </Button>
            <InsulinConceptsModal open={showConcepts} onClose={() => setShowConcepts(false)} />
            {/* DIL Budget Meter преместен в AppShell tab bar (iter222) — избягваме дублиране */}
            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  data-testid="language-toggle"
                  className={`h-8 w-8 sm:h-9 sm:w-9 rounded-lg ${isDark ? 'text-gray-400 hover:text-white hover:bg-[hsl(223,47%,15%)]' : 'text-[#64748B] hover:text-[#1A1A2E] hover:bg-[#F1F5F9]'}`}
                  title={t('settings.language')}
                >
                  <span className="text-xs font-bold">{language.toUpperCase()}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className={`w-44 ${isDark ? 'bg-[hsl(222,47%,11%)] border-[hsl(223,47%,18%)]' : ''}`}>
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    data-testid={`lang-${lang.code}`}
                    className={`cursor-pointer flex items-center justify-between ${
                      language === lang.code
                        ? isDark ? 'bg-[hsl(223,47%,15%)] text-[#0EA5E9]' : 'bg-[#F0F9FF] text-[#0EA5E9]'
                        : ''
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold w-6">{lang.label}</span>
                      <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{lang.name}</span>
                    </div>
                    {language === lang.code && <span className="text-[#0EA5E9] text-xs font-bold">*</span>}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Info Page Link */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/')}
              data-testid="info-page-btn"
              className={`h-8 w-8 sm:h-9 sm:w-9 rounded-lg ${isDark ? 'text-gray-400 hover:text-white hover:bg-[hsl(223,47%,15%)]' : 'text-[#64748B] hover:text-[#1A1A2E] hover:bg-[#F1F5F9]'}`}
              title={language === "bg" ? "Информация" : "Info"}
            >
              <Info className="w-4 h-4" />
            </Button>

            {/* iter575 — Премахнати ModeToggle / Customize / Edit бутони по
                потребителска заявка. Dashboard-ът сега е унифициран Command Center,
                без режими и без редакция в-place. */}

            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              data-testid="theme-toggle"
              className={`h-8 w-8 sm:h-9 sm:w-9 rounded-lg ${isDark ? 'text-gray-400 hover:text-white hover:bg-[hsl(223,47%,15%)]' : 'text-[#64748B] hover:text-[#1A1A2E] hover:bg-[#F1F5F9]'}`}
              title={t('settings.theme')}
            >
              {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>

            {/* iter1058 — AI Coach сигналите нямаха видима точка в UI
                (HealthHub вече не се рендира). Звънчето живее в хедъра. */}
            <CoachNudgeBell />

            {/* Subscription Status */}
            {(() => {
              const bg = language === "bg";
              const sub = user?.subscription;
              const status = sub?.status;
              if (status === "trialing") {
                return (
                  <div 
                    onClick={() => navigate('/pricing')}
                    className={`hidden sm:flex items-center space-x-2 px-3 py-2 rounded-xl cursor-pointer hover:shadow-sm transition-shadow ${
                      isDark ? 'bg-sky-900/30 border border-sky-800/50' : 'bg-gradient-to-r from-sky-50 to-cyan-50 border border-sky-100'
                    }`}
                    data-testid="subscription-display"
                  >
                    <Activity className="w-4 h-4 text-sky-500" />
                    <span className={`text-xs font-semibold ${isDark ? 'text-sky-300' : 'text-sky-700'}`}>
                      {bg ? "Пробен период" : "Trial"}
                    </span>
                  </div>
                );
              }
              if (status === "active") {
                return (
                  <div className={`hidden sm:flex items-center space-x-2 px-3 py-2 rounded-xl ${isDark ? 'bg-emerald-900/30 border border-emerald-800/50' : 'bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-100'}`} data-testid="subscription-display">
                    <Activity className="w-4 h-4 text-emerald-500" />
                    <span className={`text-xs font-semibold ${isDark ? 'text-emerald-300' : 'text-emerald-700'}`}>
                      {sub?.plan === "pro" ? "Pro" : (bg ? "Активен план" : "Active Plan")}
                    </span>
                  </div>
                );
              }
              return (
                <div 
                  className={`hidden sm:flex items-center space-x-2 px-3 py-2 rounded-xl cursor-pointer hover:shadow-sm transition-shadow ${
                    isDark ? 'bg-violet-900/30 border border-violet-800/50' : 'bg-gradient-to-r from-violet-50 to-indigo-50 border border-violet-100'
                  }`}
                  onClick={() => navigate('/assessment')}
                  data-testid="subscription-display"
                >
                  <Activity className="w-4 h-4 text-violet-500" />
                  <span className={`text-xs font-semibold ${isDark ? 'text-violet-300' : 'text-violet-700'}`}>
                    {bg ? "Нова оценка" : "New Assessment"}
                  </span>
                </div>
              );
            })()}

            {/* User Menu Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center space-x-2 focus:outline-none" data-testid="user-menu-btn">
                  <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-gradient-to-br from-pink-500 to-violet-600 flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {user?.name?.charAt(0)?.toUpperCase() || "U"}
                    </span>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className={`w-56 ${isDark ? 'bg-[hsl(222,47%,11%)] border-[hsl(223,47%,18%)]' : ''}`}
                onCloseAutoFocus={(e) => e.preventDefault()}
              >
                <div className="px-3 py-2">
                  <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-[#1A1A2E]'}`}>{user?.name}</p>
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-[#64748B]'}`}>{user?.email}</p>
                  <div className="sm:hidden flex items-center mt-1 space-x-1">
                    <Activity className="w-3 h-3 text-sky-500" />
                    <span className={`text-xs font-medium ${isDark ? 'text-sky-300' : 'text-sky-700'}`}>
                      {user?.subscription?.status === "trialing" ? (language === "bg" ? "Пробен период" : "Trial") :
                       user?.subscription?.status === "active" ? (language === "bg" ? "Активен план" : "Active Plan") :
                       (language === "bg" ? "Безплатен" : "Free")}
                    </span>
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/dashboard/profile')} data-testid="edit-profile-btn" className="cursor-pointer">
                  <User className="w-4 h-4 mr-2" />
                  {t('profile.title')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/assessment?retake=true')} data-testid="retake-assessment-btn" className="cursor-pointer">
                  <ClipboardList className="w-4 h-4 mr-2" />
                  {language === "bg" ? "Повтори оценка" : "Retake Assessment"}
                </DropdownMenuItem>
                {user?.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="admin-panel-btn" className="cursor-pointer">
                    <Shield className="w-4 h-4 mr-2" />
                    {t('header.admin')}
                  </DropdownMenuItem>
                )}
                {user?.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate('/admin/patients')} data-testid="admin-patients-btn" className="cursor-pointer">
                    <Shield className="w-4 h-4 mr-2" />
                    {language === "bg" ? "Пациенти (Влез като)" : "Patients (Impersonate)"}
                  </DropdownMenuItem>
                )}
                {user?.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate('/admin/audit-log')} data-testid="admin-audit-log-btn" className="cursor-pointer">
                    <Shield className="w-4 h-4 mr-2" />
                    {language === "bg" ? "Audit Log" : "Audit Log"}
                  </DropdownMenuItem>
                )}
                {user?.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate('/admin/bhi-risk-factors')} data-testid="admin-bhi-risk-btn" className="cursor-pointer">
                    <Shield className="w-4 h-4 mr-2" />
                    {language === "bg" ? "BHI Risk Factors (YAML)" : "BHI Risk Factors (YAML)"}
                  </DropdownMenuItem>
                )}
                {(user?.role === "admin" || (user?.role === "clinician" && user?.clinician_status === "approved")) && (
                  <DropdownMenuItem onClick={() => navigate('/doctor')} data-testid="doctor-portal-btn" className="cursor-pointer">
                    <Activity className="w-4 h-4 mr-2" />
                    {language === "bg" ? "Доктор портал" : "Doctor Portal"}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem className="cursor-pointer">
                  <RestartTourButton user={user} />
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn" className="cursor-pointer text-red-500 focus:text-red-500">
                  <LogOut className="w-4 h-4 mr-2" />
                  {t('header.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`md:hidden h-8 w-8 rounded-lg ${isDark ? 'text-gray-400 hover:text-white' : 'text-[#64748B] hover:text-[#1A1A2E]'}`}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation — Table-of-contents премахнато: AppShell + MobileBottomNav
          дават primary navigation; quick links се показват в Dashboard. */}
      {mobileMenuOpen && (
        <div className={`md:hidden border-t ${isDark ? 'bg-[hsl(222,47%,8%)] border-[hsl(223,47%,18%)]' : 'bg-white border-[#E2E8F0]'}`} data-testid="mobile-menu">
          <div className="px-4 py-3 space-y-1">
            <button
              onClick={() => { onEditProfile?.(); setMobileMenuOpen(false); }}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                isDark ? 'text-gray-400 hover:bg-[hsl(223,47%,12%)]' : 'text-[#64748B] hover:bg-[#F8FAFC]'
              }`}
            >
              <User className="w-5 h-5" />
              <span>{t("profile.title")}</span>
            </button>
            <button
              onClick={() => { navigate('/assessment'); setMobileMenuOpen(false); }}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                isDark ? 'text-gray-400 hover:bg-[hsl(223,47%,12%)]' : 'text-[#64748B] hover:bg-[#F8FAFC]'
              }`}
            >
              <ClipboardList className="w-5 h-5" />
              <span>{bg ? "Повтори оценка" : "Retake Assessment"}</span>
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
